'use client';

import { cn } from '@coas/ui/lib';
import { useRouter, useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import IconCancel from '~/shared/assets/icons/cancel.svg';
// import IconSearch from '~/shared/assets/icons/search.svg';
import type { PropsOf } from '~/shared/lib';

export const useSearch = () => {
    const router = useRouter();
    const searchParams = useSearchParams();

    const search = useMemo(() => searchParams.get('search') ?? '', [searchParams]);
    const setSearch = useCallback(
        (value?: string) => {
            const params = new URLSearchParams(searchParams);

            if (value) params.set('search', value);
            else params.delete('search');

            router.push(`/products?${params}`);
        },
        [searchParams, router.push],
    );

    return useMemo(() => [search, setSearch] as const, [search, setSearch]);
};

type SearchProps = Omit<PropsOf<'form'>, 'onSubmit'>;

export const Search: React.FC<SearchProps> = ({ className, ...props }) => {
    const [search, setSearch] = useSearch();

    const { register, setValue, handleSubmit } = useForm({
        defaultValues: {
            search,
        },
        mode: 'onTouched',
    });

    useEffect(() => {
        setValue('search', search);
    }, [search, setValue]);

    return (
        <form
            onSubmit={handleSubmit(data => {
                setSearch(data.search);
                if (document.activeElement instanceof HTMLElement) {
                    document.activeElement.blur();
                }
            })}
            className={cn('relative flex-1', className)}
            {...props}
        >
            <input
                type={'text'}
                placeholder={'Поиск'}
                className={
                    'w-full rounded-full border-2 border-neutral-600 bg-neutral-50 px-4 py-1 pr-10 text-18 outline-none outline-2 focus:border-blue-600'
                }
                {...register('search')}
            />
            <div
                className={
                    'absolute inset-y-0 right-3 my-auto flex h-fit rounded-2 outline-2 outline-neutral-400 focus-visible:outline'
                }
            >
                {!!search && (
                    <button type={'button'} onClick={() => setSearch(undefined)} className={'appearance-none'}>
                        <IconCancel className={'h-6 w-6 stroke-neutral-400'} />
                    </button>
                )}
                {/* <IconSearch className={'h-6 w-6 stroke-neutral-400'} /> */}
            </div>
        </form>
    );
};
